import React, { useState } from 'react';
import cx from 'classnames';
import { forTemplate } from 'app/common/lib/util/i18n';
import {
  useSelectCoverQuery,
  useUpdateCardCoverMutation,
} from '@trello/graphql/generated';
import {
  smallestPreviewBiggerThan,
  biggestPreview,
} from 'app/common/lib/util/preview';
import { Upload } from './Upload';
import { track } from '@trello/analytics';
import {
  Analytics,
  Action,
  Source,
  ActionSubject,
  ActionSubjectId,
} from '@trello/atlassian-analytics';

import styles from './SelectAttachmentCover.less';

const format = forTemplate('card_cover_chooser');

interface SelectAttachmentCoverProps {
  cardId: string;
}

export const SelectAttachmentCover: React.FunctionComponent<SelectAttachmentCoverProps> = ({
  cardId,
}) => {
  const { data } = useSelectCoverQuery({
    variables: { cardId },
    fetchPolicy: 'cache-only',
  });

  if (!data?.card) {
    throw new Error(
      'Failed to load card details from cache for SelectAttachmentCover.',
    );
  }

  const card = data.card;
  const { attachments } = card;
  const idAttachmentCover = card.cover?.idAttachment;

  const [showMore, setShowMore] = useState(false);
  const [idFirstAttachment, setIdFirstAttachment] = useState(idAttachmentCover);
  const [selectedBeyondCutOff, setSelectedBeyondCutOff] = useState(false);

  const [updateCardCover] = useUpdateCardCoverMutation();

  if (!data?.card) {
    throw new Error(
      'Failed to load card details from cache for SelectAttachmentCover.',
    );
  }

  const containers = {
    organization: {
      id: card?.board.idOrganization,
    },
    board: {
      id: card?.board.id,
    },
    card: {
      id: card?.id,
    },
  };

  const setCover = async (
    cover: {
      idAttachment?: string;
    } | null,
  ) => {
    await updateCardCover({
      variables: {
        cardId,
        cover,
      },
      optimisticResponse: {
        __typename: 'Mutation',
        updateCardCover: {
          __typename: 'Card',
          id: cardId,
          cover: cover
            ? {
                __typename: 'Card_Cover',
                ...card.cover,
                idAttachment: cover.idAttachment,
                idUploadedBackground: null,
                color: null,
              }
            : {
                __typename: 'Card_Cover',
                ...card.cover,
                idAttachment: null,
                idUploadedBackground: null,
                color: null,
              },
          idAttachmentCover: cover?.idAttachment,
        },
      },
    });

    if (cover) {
      Analytics.sendTrackEvent({
        action: Action.updated,
        actionSubject: ActionSubject.cover,
        source: Source.cardCoverInlineDialog,
        attributes: {
          type: 'attachment',
          value: cover.idAttachment,
        },
        containers,
      });
    } else {
      Analytics.sendTrackEvent({
        action: Action.removed,
        actionSubject: ActionSubject.cover,
        source: Source.cardCoverInlineDialog,
        attributes: {
          type: 'attachment',
        },
        containers,
      });
    }
  };

  const potentialAttachmentCovers = [];

  for (const attachment of attachments) {
    const preview =
      smallestPreviewBiggerThan(attachment.previews, 256) ||
      biggestPreview(attachment.previews);

    if (!preview || !preview.url) {
      continue;
    }

    const potentialAttachmentCover = {
      id: attachment.id,
      edgeColor: attachment.edgeColor || 'transparent',
      thumbnailUrl: preview.url,
      selected: idAttachmentCover === attachment.id,
    };

    if (idFirstAttachment && attachment.id === idFirstAttachment) {
      potentialAttachmentCovers.unshift(potentialAttachmentCover);
    } else {
      potentialAttachmentCovers.push(potentialAttachmentCover);
    }
  }

  const potentialAttachmentCoversToShow = showMore
    ? potentialAttachmentCovers
    : potentialAttachmentCovers.slice(0, 6);

  return (
    <>
      <h4 className={styles.heading}>{format('attachments')}</h4>
      {potentialAttachmentCovers.length > 0 && (
        <div className={styles.attachmentTiles}>
          {potentialAttachmentCoversToShow.map(
            ({ id, edgeColor, thumbnailUrl, selected }, index) => (
              <button
                key={id}
                className={cx('button', styles.attachmentTilesItem, {
                  [styles.selected]: selected,
                })}
                style={{
                  backgroundColor: edgeColor,
                  backgroundImage: `url('${thumbnailUrl}')`,
                  backgroundSize: 'contain',
                }}
                onClick={() => {
                  if (selected) {
                    setCover(null);
                    track(
                      'Card Cover Chooser',
                      'Toggled Cover Off',
                      'Attachment',
                    );
                  } else {
                    setCover({
                      idAttachment: id,
                    });

                    if (index > 5) {
                      setSelectedBeyondCutOff(true);
                    } else {
                      setSelectedBeyondCutOff(false);
                    }

                    track('Card Cover Chooser', 'Chose Cover', 'Attachment');
                  }

                  Analytics.sendUIEvent({
                    action: Action.clicked,
                    actionSubject: ActionSubject.tile,
                    actionSubjectId: ActionSubjectId.cardCoverAttachmentTile,
                    source: Source.cardCoverInlineDialog,
                    attributes: {
                      value: id,
                    },
                    containers,
                  });
                }}
              />
            ),
          )}
        </div>
      )}
      {potentialAttachmentCovers.length > 6 && !showMore && (
        <button
          className={cx('button', styles.showMoreButton)}
          onClick={() => {
            Analytics.sendClickedButtonEvent({
              buttonName: ActionSubjectId.cardCoverAttachmentShowMoreButton,
              source: Source.cardCoverInlineDialog,
              containers,
            });
            setShowMore(true);
          }}
        >
          {format('show-more')}
        </button>
      )}
      {showMore && (
        <button
          className={cx('button', styles.showFewerButton)}
          onClick={() => {
            Analytics.sendClickedButtonEvent({
              buttonName: ActionSubjectId.cardCoverAttachmentShowFewerButton,
              source: Source.cardCoverInlineDialog,
              containers,
            });

            setShowMore(false);

            if (selectedBeyondCutOff) {
              setIdFirstAttachment(card.cover?.idAttachment);
            }
          }}
        >
          {format('show-fewer')}
        </button>
      )}
      <Upload
        cardId={cardId}
        className={cx(styles.uploadButton, {
          [styles.hasNoAttachments]: potentialAttachmentCovers.length === 0,
          [styles.hasPaginatedTiles]: potentialAttachmentCovers.length > 6,
        })}
        hideHeading
      />
    </>
  );
};
